/**
 * Global Patient Packages — pure logic (PRD.263).
 *
 * Dependency-free filter / sort / summary helpers for the global Patient
 * Packages operations page. Import-free so they can be unit-tested with Node's
 * built-in runner and reused by the page (which loads all org package rows via
 * `qPatientPackagesGlobal` and filters/sorts client-side).
 */

/** Minimal shape the filter/sort/summary helpers read from a package row. */
export interface GlobalPackageRowLike {
  patient_name: string | null;
  patient_number: string | null;
  package_name: string | null;
  package_code: string | null;
  payment_status: string | null;
  redemption_status: string | null;
  valid_until: string | null;
  remaining_quantity: number | null;
  has_pending_release: number | null;
  updated_at: string | null;
}

export interface GlobalPackageFilters {
  patientQuery?: string;
  packageQuery?: string;
  /** Empty/omitted = any. */
  paymentStatus?: readonly string[];
  redemptionStatus?: readonly string[];
  validUntilFrom?: string | null;
  validUntilTo?: string | null;
  expiringSoon?: boolean;
  hasRemainingBenefits?: boolean;
  hasPendingRelease?: boolean;
}

/** Days ahead that counts as "expiring soon". */
export const EXPIRING_SOON_DAYS = 30;

/** Add whole days to a `YYYY-MM-DD` date, returning `YYYY-MM-DD`. */
export function addDaysToDate(date: string, days: number): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(date);
  if (!m) return null;
  // Use UTC arithmetic to avoid timezone drift; date-only so no DST concerns.
  const base = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const d = new Date(base + days * 86400000);
  const y = d.getUTCFullYear();
  const mo = d.getUTCMonth() + 1;
  const da = d.getUTCDate();
  return `${y}-${pad2(mo)}-${pad2(da)}`;
}

/**
 * Whether a package is expiring within the window: it has an expiry that is
 * today-or-later and no later than `today + EXPIRING_SOON_DAYS`.
 */
export function isExpiringSoon(validUntil: string | null, today: string): boolean {
  if (!validUntil) return false;
  const until = validUntil.slice(0, 10);
  const cutoff = addDaysToDate(today, EXPIRING_SOON_DAYS);
  if (!cutoff) return false;
  return until >= today && until <= cutoff;
}

/** Filter global package rows by the operations filters (all combinable). */
export function filterGlobalPackages<T extends GlobalPackageRowLike>(
  rows: readonly T[],
  filters: GlobalPackageFilters,
  today: string,
): T[] {
  const patientQ = (filters.patientQuery ?? '').trim().toLowerCase();
  const packageQ = (filters.packageQuery ?? '').trim().toLowerCase();
  const paySet =
    filters.paymentStatus && filters.paymentStatus.length ? new Set(filters.paymentStatus) : null;
  const redSet =
    filters.redemptionStatus && filters.redemptionStatus.length
      ? new Set(filters.redemptionStatus)
      : null;

  return rows.filter((r) => {
    if (patientQ) {
      const hay = `${r.patient_name ?? ''} ${r.patient_number ?? ''}`.toLowerCase();
      if (!hay.includes(patientQ)) return false;
    }
    if (packageQ) {
      const hay = `${r.package_name ?? ''} ${r.package_code ?? ''}`.toLowerCase();
      if (!hay.includes(packageQ)) return false;
    }
    if (paySet && !paySet.has(r.payment_status ?? '')) return false;
    if (redSet && !redSet.has(r.redemption_status ?? '')) return false;
    if (
      filters.validUntilFrom &&
      (!r.valid_until || r.valid_until.slice(0, 10) < filters.validUntilFrom)
    ) {
      return false;
    }
    if (
      filters.validUntilTo &&
      (!r.valid_until || r.valid_until.slice(0, 10) > filters.validUntilTo)
    ) {
      return false;
    }
    if (filters.expiringSoon && !isExpiringSoon(r.valid_until, today)) return false;
    if (filters.hasRemainingBenefits && (r.remaining_quantity ?? 0) <= 0) return false;
    if (filters.hasPendingRelease && r.has_pending_release !== 1) return false;
    return true;
  });
}

/**
 * Default operational sort (PRD.263 §3): active packages first, then by
 * validity ascending with no-expiry last, then most recently updated.
 */
export function sortGlobalPackages<T extends GlobalPackageRowLike>(rows: readonly T[]): T[] {
  return [...rows].sort((a, b) => {
    const aActive = a.redemption_status === 'active' ? 0 : 1;
    const bActive = b.redemption_status === 'active' ? 0 : 1;
    if (aActive !== bActive) return aActive - bActive;

    // valid_until ascending, nulls (no expiry) last.
    const av = a.valid_until ? a.valid_until.slice(0, 10) : null;
    const bv = b.valid_until ? b.valid_until.slice(0, 10) : null;
    if (av !== bv) {
      if (av === null) return 1;
      if (bv === null) return -1;
      return av < bv ? -1 : 1;
    }

    // updated_at descending.
    const au = a.updated_at ?? '';
    const bu = b.updated_at ?? '';
    return au < bu ? 1 : au > bu ? -1 : 0;
  });
}

export interface GlobalPackageSummary {
  activePackages: number;
  partiallyPaid: number;
  expiringSoon: number;
  pendingRelease: number;
}

/**
 * Summary-card counts derived from the full org package set (PRD.263 §2).
 * "Redeemed Today" is counted separately from the redemption ledger.
 */
export function computeGlobalPackageSummary(
  rows: readonly GlobalPackageRowLike[],
  today: string,
): GlobalPackageSummary {
  let activePackages = 0;
  let partiallyPaid = 0;
  let expiringSoon = 0;
  let pendingRelease = 0;
  for (const r of rows) {
    const active = r.redemption_status === 'active' && r.payment_status !== 'voided';
    if (active) activePackages += 1;
    if (r.payment_status === 'partially_paid' && r.redemption_status !== 'voided') {
      partiallyPaid += 1;
    }
    if (r.redemption_status === 'active' && isExpiringSoon(r.valid_until, today)) {
      expiringSoon += 1;
    }
    if (r.has_pending_release === 1) pendingRelease += 1;
  }
  return { activePackages, partiallyPaid, expiringSoon, pendingRelease };
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}
